import React, { useEffect, useMemo, useState } from "react";
import {
  listarClientesZenMobile,
  bloquearClienteZenMobile,
  desbloquearClienteZenMobile,
  listarRevendasDropdown,
  bonificarLicencaZenMobile,
  desbonificarLicencaZenMobile,
  cancelarLicencaZenMobile,
  reativarLicencaZenMobile,
} from "../services/api";

import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle, XCircle, Edit, Search, Plus, ShieldOff, Unlock, Gift, Ban, RotateCcw } from "lucide-react";
import BlockModal from "./common/BlockModal";
import CancelarLicencaModal from "./common/CancelarLicencaModal";
import BonificarLicencaModal from "./common/BonificarLicencaModal";
import { useFeedbackAlert } from "./common/useFeedbackAlert";

const DEFAULT_BLOCK_MESSAGE =
  "Entre em contato com o departamento financeiro: (11)5924-9068 Opção 3";

const isCancelada = (c) => !!(c?.cancelada ?? c?.Cancelada);
const isBonificada = (c) => !!(c?.bonificada ?? c?.Bonificada);

const ListaZenMobile = ({ token }) => {
  const { showError, showSuccess, FeedbackAlert } = useFeedbackAlert();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const qp = (k, def = "") => searchParams.get(k) ?? def;

  // filtros (URL)
  const [busca, setBusca] = useState(qp("search", ""));
  const [revendaId, setRevendaId] = useState(qp("revendaId", ""));
  const [ativo, setAtivo] = useState(qp("ativo", "all")); // all | 1 | 0
  const [cancelada, setCancelada] = useState(qp("cancelada", "")); // "" = ativas | "1" = só canceladas

  // dados
  const [clientes, setClientes] = useState([]);
  const [revendas, setRevendas] = useState([]);

  // paginação (se quiser UI depois)
  const [page, setPage] = useState(Number(qp("page", "1")) || 1);
  const [pageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(1);

  // modal bloqueio
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [selectedClientToBlock, setSelectedClientToBlock] = useState(null);

  // modais bonificar / cancelar
  const [showBonificarModal, setShowBonificarModal] = useState(false);
  const [showCancelarModal, setShowCancelarModal] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const fetchRevendas = async () => {
    try {
      const r = await listarRevendasDropdown(token);
      const data = r?.data;

      const list =
        Array.isArray(data) ? data :
        Array.isArray(data?.items) ? data.items :
        Array.isArray(data?.Items) ? data.Items :
        [];

      setRevendas(list);
    } catch (e) {
      console.error("Erro ao listar revendas:", e);
      setRevendas([]);
    }
  };

  const fetchClientes = async () => {
    try {
      const resp = await listarClientesZenMobile(token, {
        page,
        pageSize,
        search: busca || undefined,
        revendaId: revendaId || undefined,
        ativo: ativo === "all" ? undefined : ativo,
        cancelada: cancelada || undefined,
        sortBy: "criadoem",
        desc: true,
      });

      const data = resp?.data;

      // PagedResponse
      const items =
        Array.isArray(data) ? data :
        Array.isArray(data?.items) ? data.items :
        Array.isArray(data?.Items) ? data.Items :
        [];

      setClientes(items);
      setTotalPages(Number(data?.totalPages || data?.TotalPages || 1));
    } catch (e) {
      console.error("Erro ao listar clientes ZenMobile:", e);
      setClientes([]);
      setTotalPages(1);
    }
  };

  // carrega revendas 1x
  useEffect(() => {
    fetchRevendas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // recarrega lista quando filtros/página mudarem
  useEffect(() => {
    fetchClientes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, busca, revendaId, ativo, cancelada, page]);

  // sync querystring
  useEffect(() => {
    const p = new URLSearchParams(searchParams);

    if (busca) p.set("search", busca);
    else p.delete("search");

    if (revendaId) p.set("revendaId", revendaId);
    else p.delete("revendaId");

    if (ativo && ativo !== "all") p.set("ativo", ativo);
    else p.delete("ativo");

    if (cancelada) p.set("cancelada", cancelada);
    else p.delete("cancelada");

    if (page && page !== 1) p.set("page", String(page));
    else p.delete("page");

    setSearchParams(p, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [busca, revendaId, ativo, cancelada, page]);

  const handleEditar = (id) => navigate(`/licenciamento/zenmobile/editar/${id}`);

  const handleToggleBlock = async (id, isAtivo) => {
    if (isAtivo) {
      setSelectedClientToBlock(id);
      setShowBlockModal(true);
      return;
    }

    if (!window.confirm("Tem certeza que deseja desbloquear este cliente?")) return;

    try {
      await desbloquearClienteZenMobile(id, token);
      showSuccess("Cliente desbloqueado com sucesso!");
      await fetchClientes();
    } catch (error) {
      console.error("Erro ao desbloquear cliente:", error);
      showError(error, "Erro ao desbloquear cliente.");
    }
  };

  const handleConfirmBlock = async (message) => {
    try {
      await bloquearClienteZenMobile(selectedClientToBlock, { MensagemBloqueio: message }, token);
      showSuccess("Cliente bloqueado com sucesso!");
      setShowBlockModal(false);
      setSelectedClientToBlock(null);
      await fetchClientes();
    } catch (error) {
      console.error("Erro ao bloquear cliente:", error);
      showError(error, "Erro ao bloquear cliente.");
    }
  };

  const openBonificar = (id) => {
    setSelectedId(id);
    setShowBonificarModal(true);
  };

  const openCancelar = (id) => {
    setSelectedId(id);
    setShowCancelarModal(true);
  };

  const handleConfirmBonificar = async (motivo) => {
    if (!selectedId) return;
    setActionLoading(true);
    try {
      await bonificarLicencaZenMobile(selectedId, { motivo: motivo || undefined }, token);
      showSuccess("Licença bonificada com sucesso!");
      setShowBonificarModal(false);
      setSelectedId(null);
      await fetchClientes();
    } catch (error) {
      console.error("Erro ao bonificar:", error);
      showError(error, "Erro ao bonificar licença.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleDesbonificar = async (id) => {
    if (!window.confirm("Remover bonificação desta licença?")) return;
    setActionLoading(true);
    try {
      await desbonificarLicencaZenMobile(id, token);
      showSuccess("Bonificação removida.");
      await fetchClientes();
    } catch (error) {
      console.error("Erro ao desbonificar:", error);
      showError(error, "Erro ao desbonificar licença.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleConfirmCancelar = async (motivo) => {
    if (!selectedId) return;
    setActionLoading(true);
    try {
      await cancelarLicencaZenMobile(selectedId, { motivo: motivo }, token);
      showSuccess("Licença cancelada.");
      setShowCancelarModal(false);
      setSelectedId(null);
      await fetchClientes();
    } catch (error) {
      console.error("Erro ao cancelar licença:", error);
      showError(error, "Erro ao cancelar licença.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleReativar = async (id) => {
    if (!window.confirm("Reativar esta licença?")) return;
    setActionLoading(true);
    try {
      await reativarLicencaZenMobile(id, token);
      showSuccess("Licença reativada.");
      await fetchClientes();
    } catch (error) {
      console.error("Erro ao reativar:", error);
      showError(error, "Erro ao reativar licença.");
    } finally {
      setActionLoading(false);
    }
  };

  // (opcional) resetar para página 1 ao mudar filtros
  useEffect(() => {
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [busca, revendaId, ativo, cancelada]);

  const items = useMemo(() => clientes, [clientes]);

  return (
  <>
    <FeedbackAlert />
  <div
    className="
      zen-card
      rounded-2xl p-6 shadow-sm
      dark:backdrop-blur
    "
  >
    {/* header */}
    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
          Licenças ZenMobile
        </h2>
        <p className="text-sm text-gray-500 dark:text-slate-300/80">
          Filtros: Revenda + Status + Canceladas
        </p>
      </div>

      <button
        className="
          inline-flex items-center justify-center px-4 py-2 rounded-xl font-semibold text-sm transition
          bg-blue-600 text-white hover:bg-blue-500
        "
        onClick={() => navigate("/licenciamento/zenmobile/novo")}
      >
        <Plus size={18} className="mr-2" />
        Novo ZenMobile
      </button>
    </div>

    {/* filtros */}
    <div
      className="
        rounded-2xl p-4 mb-6 zen-card border-gray-200
        dark:bg-white/5 dark:border-white/10
      "
    >
      <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
        <div className="md:col-span-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-1">
            Buscar
          </label>
          <div className="relative">
            <Search
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-slate-400"
              size={18}
            />
            <input
              type="text"
              placeholder="Cliente / Descrição / Código instalação"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              className="
                pl-10 pr-4 py-2.5 w-full rounded-xl border outline-none transition
                zen-card text-gray-900 placeholder:text-gray-400
                focus:ring-2 focus:ring-blue-500/60
                dark:bg-slate-950/40 dark:border-white/10 dark:text-white dark:placeholder:text-slate-400
                dark:focus:ring-blue-400/40
              "
            />
          </div>
        </div>

        <div className="md:col-span-3">
          <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-1">
            Revenda
          </label>
          <select
            value={revendaId}
            onChange={(e) => setRevendaId(e.target.value)}
            className="
              w-full p-2.5 rounded-xl border outline-none transition
              zen-card border-gray-300 text-gray-900
              focus:ring-2 focus:ring-blue-500/60
              dark:bg-slate-950/40 dark:border-white/10 dark:text-white
              dark:focus:ring-blue-400/40
            "
          >
            <option value="">Todas</option>
            <option value="0">MASTER</option>
            {revendas.map((r) => (
              <option key={r.id} value={String(r.id)}>
                {r.nomeFantasia || r.razaoSocial || `Revenda ${r.id}`}
              </option>
            ))}
          </select>
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-1">
            Status
          </label>
          <select
            value={ativo}
            onChange={(e) => setAtivo(e.target.value)}
            className="
              w-full p-2.5 rounded-xl border outline-none transition
              border-gray-300 text-gray-900
              focus:ring-2 focus:ring-blue-500/60
              dark:bg-slate-950/40 dark:border-white/10 dark:text-white
              dark:focus:ring-blue-400/40
            "
          >
            <option value="all">Todos</option>
            <option value="1">Ativos</option>
            <option value="0">Bloqueados</option>
          </select>
        </div>

        <div className="md:col-span-3">
          <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-1">
            Cancelamento
          </label>
          <select
            value={cancelada}
            onChange={(e) => setCancelada(e.target.value)}
            className="
              w-full p-2.5 rounded-xl border outline-none transition
              border-gray-300 text-gray-900
              focus:ring-2 focus:ring-blue-500/60
              dark:bg-slate-950/40 dark:border-white/10 dark:text-white
              dark:focus:ring-blue-400/40
            "
          >
            <option value="">Ativas (não canceladas)</option>
            <option value="1">Canceladas</option>
          </select>
        </div>
      </div>
    </div>

    {/* tabela */}
    <div className="overflow-x-auto">
      <div
        className="
          min-w-full rounded-2xl
          zen-card
          dark:border-white/10 dark:bg-white/5
        "
      >
        <table className="min-w-full">
          <thead className="bg-gray-50 dark:bg-white/5">
            <tr>
              <th className="text-left py-3 px-4 font-semibold text-gray-600 dark:text-slate-200">
                Cliente
              </th>
              <th className="text-left py-3 px-4 font-semibold text-gray-600 dark:text-slate-200">
                Descrição
              </th>
              <th className="text-left py-3 px-4 font-semibold text-gray-600 dark:text-slate-200">
                Qtde Dispositivos
              </th>
              <th className="text-left py-3 px-4 font-semibold text-gray-600 dark:text-slate-200">
                Cód. Instalação
              </th>
              <th className="text-center py-3 px-4 font-semibold text-gray-600 dark:text-slate-200">
                Status
              </th>
              <th className="text-center py-3 px-4 font-semibold text-gray-600 dark:text-slate-200">
                Ações
              </th>
            </tr>
          </thead>

          <tbody className="text-gray-700 dark:text-slate-200">
            {items.map((cliente) => {
              const canceladaFlag = isCancelada(cliente);
              const bonificadaFlag = isBonificada(cliente);

              return (
              <tr
                key={cliente.id}
                className="
                  border-t border-gray-200 hover:bg-gray-50 transition
                  dark:border-white/10 dark:hover:bg-white/5
                "
              >
                <td className="py-3 px-4">{cliente.nomeFantasia || "-"}</td>
                <td className="py-3 px-4">{cliente.descricao || "-"}</td>
                <td className="py-3 px-4">{cliente.qtdDispositivos ?? "-"}</td>
                <td className="py-3 px-4">{cliente.codigoInstalacao ?? "-"}</td>

                <td className="text-center py-3 px-4">
                  <div className="flex flex-col items-center gap-1">
                    {cliente.ativo ? (
                      <CheckCircle className="text-green-500 inline" size={20} />
                    ) : (
                      <XCircle className="text-red-500 inline" size={20} />
                    )}
                    {bonificadaFlag ? (
                      <span className="text-[10px] font-semibold uppercase tracking-wide text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full dark:bg-emerald-500/20 dark:text-emerald-200">
                        Bonificada
                      </span>
                    ) : null}
                    {canceladaFlag ? (
                      <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-700 bg-gray-200 px-2 py-0.5 rounded-full dark:bg-white/10 dark:text-slate-300">
                        Cancelada
                      </span>
                    ) : null}
                  </div>
                </td>

                <td className="text-center py-3 px-4">
                  <div className="inline-flex items-center gap-1">
                    {canceladaFlag ? (
                      <button
                        className="p-2 rounded-full transition text-emerald-600 hover:bg-emerald-100 dark:text-emerald-300 dark:hover:bg-emerald-500/10"
                        onClick={() => handleReativar(cliente.id)}
                        title="Reativar"
                        disabled={actionLoading}
                      >
                        <RotateCcw size={18} />
                      </button>
                    ) : (
                      <>
                        <button
                          className="
                            p-2 rounded-full transition
                            text-blue-600 hover:bg-blue-100
                            dark:text-blue-300 dark:hover:bg-blue-500/10
                          "
                          onClick={() => handleEditar(cliente.id)}
                          title="Editar"
                        >
                          <Edit size={18} />
                        </button>

                        <button
                          className={`
                            p-2 rounded-full transition
                            ${
                              cliente.ativo
                                ? "text-red-600 hover:bg-red-100 dark:hover:bg-red-500/10"
                                : "text-green-600 hover:bg-green-100 dark:hover:bg-green-500/10"
                            }
                          `}
                          onClick={() => handleToggleBlock(cliente.id, cliente.ativo)}
                          title={cliente.ativo ? "Bloquear" : "Desbloquear"}
                        >
                          {cliente.ativo ? <ShieldOff size={18} /> : <Unlock size={18} />}
                        </button>

                        {bonificadaFlag ? (
                          <button
                            className="p-2 rounded-full transition text-amber-700 hover:bg-amber-100 dark:text-amber-300 dark:hover:bg-amber-500/10"
                            onClick={() => handleDesbonificar(cliente.id)}
                            title="Desbonificar"
                            disabled={actionLoading}
                          >
                            <Ban size={18} />
                          </button>
                        ) : (
                          <button
                            className="p-2 rounded-full transition text-emerald-600 hover:bg-emerald-100 dark:text-emerald-300 dark:hover:bg-emerald-500/10"
                            onClick={() => openBonificar(cliente.id)}
                            title="Bonificar"
                            disabled={actionLoading}
                          >
                            <Gift size={18} />
                          </button>
                        )}

                        <button
                          className="p-2 rounded-full transition text-gray-600 hover:bg-gray-100 dark:text-slate-300 dark:hover:bg-white/10"
                          onClick={() => openCancelar(cliente.id)}
                          title="Cancelar licença"
                          disabled={actionLoading}
                        >
                          <XCircle size={18} />
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
              );
            })}

            {items.length === 0 && (
              <tr>
                <td
                  colSpan={6}
                  className="py-10 text-center text-gray-500 dark:text-slate-300/80"
                >
                  Nenhuma licença ZenMobile encontrada.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>

    {/* paginação */}
    {totalPages > 1 && (
      <div className="flex justify-end gap-2 mt-4">
        <button
          className="
            px-3 py-2 rounded-xl border text-sm font-semibold transition
            bg-gray-100 text-gray-800 border-gray-200 hover:bg-gray-200
            disabled:opacity-50 disabled:cursor-not-allowed
            dark:bg-white/5 dark:text-white dark:border-white/10 dark:hover:bg-white/10
          "
          disabled={page <= 1}
          onClick={() => setPage((p) => Math.max(1, p - 1))}
        >
          Anterior
        </button>

        <div className="px-3 py-2 text-sm text-gray-600 dark:text-slate-300/80">
          Página {page} de {totalPages}
        </div>

        <button
          className="
            px-3 py-2 rounded-xl border text-sm font-semibold transition
            bg-gray-100 text-gray-800 border-gray-200 hover:bg-gray-200
            disabled:opacity-50 disabled:cursor-not-allowed
            dark:bg-white/5 dark:text-white dark:border-white/10 dark:hover:bg-white/10
          "
          disabled={page >= totalPages}
          onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
        >
          Próxima
        </button>
      </div>
    )}

    <BlockModal
      show={showBlockModal}
      onClose={() => setShowBlockModal(false)}
      onConfirm={handleConfirmBlock}
      defaultMessage={DEFAULT_BLOCK_MESSAGE}
    />

    <BonificarLicencaModal
      show={showBonificarModal}
      onClose={() => { setShowBonificarModal(false); setSelectedId(null); }}
      onConfirm={handleConfirmBonificar}
      loading={actionLoading}
    />

    <CancelarLicencaModal
      show={showCancelarModal}
      onClose={() => { setShowCancelarModal(false); setSelectedId(null); }}
      onConfirm={handleConfirmCancelar}
      loading={actionLoading}
    />
  </div>
  </>
);
};

export default ListaZenMobile;
